/* GENTCREATE — in-place zoom for the personalize preview.
 *
 * Replaces the old lightbox, which opened a second pop-up on top of a drawer
 * to do something the image can do where it already is. It also never worked
 * as designed: it declared position:fixed;inset:0 but sat inside
 * .personalize-drawer__body, which carries backdrop-filter and was therefore
 * its containing block, so .personalize-drawer__panel's overflow:hidden
 * clipped it. It was neither full-screen nor outside the panel.
 *
 * This mounts on [data-zoom-trigger] — an attribute already present on the
 * image container and referenced by nothing else in the theme, so it costs no
 * new markup.
 *
 * The zoom is a transform on the image with the origin under the pointer, so
 * the point you aimed at is the point that grows. The source is swapped to a
 * larger CDN rendition on the way in, using the same width= rewrite the rest
 * of this drawer uses, and restored on the way out so a variant change never
 * inherits a zoom-sized file.
 */
(function () {
  if (window.__gcPersonalizeZoom) return;
  window.__gcPersonalizeZoom = 1;

  /* 3.4, up from 2.4. It is worth having only because the source is now
     genuinely bigger — before this the extra scale would just have magnified a
     600px file further. The CSS reads it from --gc-zoom-scale so the number
     lives in exactly one place. */
  var SCALE = 3.4;

  function boot() {
    var box = document.querySelector('[data-zoom-trigger]');
    var img = document.querySelector('[data-personalize-image]');
    if (!box || !img) return false;
    /* the initials ride the picture now, so they are transformed from the same
       origin — see the note in gc-personalize-drawer.css */
    var ov = box.querySelector('[data-initials-overlay]');

    var zoomed = false;
    var small = '';
    /* The URL zoomIn actually asked for. The src-watching observer below has to
       compare against THIS and not recompute it — recomputing needs the size,
       the size depends on the plate and the pixel ratio, and a mismatch there
       makes the observer think the src was changed by someone else and zoom
       straight back out. That is exactly what happened when bigSrc gained a
       second parameter: the observer still called it with one, got a
       "_undefinedxundefined_" URL, and cancelled every zoom on the frame the
       sharp image arrived. */
    var bigUrl = '';

    /* Shopify serves every rendition off one URL with a width parameter, and
       this drawer pins the preview to width=600 in four different places. The
       same rewrite the old lightbox used gets a sharp source for the zoom. */
    function bigSrc(src, big) {
      if (!src) return src;
      /* The legacy _WxH filename FIRST, and tolerating a _crop_* suffix. On
         these URLs the CDN serves whatever the filename asks for and ignores
         a ?width= parameter entirely, so rewriting the name is the only thing
         that works. The capture keeps _crop_center so the crop is preserved. */
      if (/_\d+x\d*(_crop_[a-z]+)?\.(jpe?g|png|webp)/i.test(src)) {
        return src.replace(/_\d+x\d*(_crop_[a-z]+)?\./i, '_' + big + 'x' + big + '$1.');
      }
      if (/([?&])width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + big);
      return src + (src.indexOf('?') !== -1 ? '&' : '?') + 'width=' + big;
    }

    /* How big the file has to be for the zoom we are about to apply, on THIS
       screen. A constant cannot know the plate's width or the pixel ratio; at
       280 CSS px and 3.4x on a 2x screen the honest answer is 1904. Capped at
       2048 because that is the largest rendition worth requesting here. */
    function needFor(scale) {
      var w = box.getBoundingClientRect().width || 280;
      var dpr = window.devicePixelRatio || 1;
      /* The arithmetic answer here was around 1900 - plate x scale x pixel
         ratio - and the old 2048 ceiling was never even reached, so the
         "zoom" was inspecting a file barely sharper than the one already on
         screen. The source is 4096 wide and this is the one moment a shopper
         is deliberately looking at the engraving up close, so the whole file
         is worth fetching. It is one image, fetched only on demand.
         Oversampling on purpose: at 4096 the glyph edges stay clean even if
         the shopper pinches past our own scale. */
      return Math.min(4096, Math.max(2048, Math.ceil(w * scale * dpr * 2)));
    }

    function originFrom(ev) {
      var r = box.getBoundingClientRect();
      var p = (ev.touches && ev.touches[0]) || ev;
      var x = ((p.clientX - r.left) / r.width) * 100;
      var y = ((p.clientY - r.top) / r.height) * 100;
      /* clamped so the edges of the picture stay reachable without the
         transform pulling empty space into frame */
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      img.style.transformOrigin = x + '% ' + y + '%';
      if (ov) ov.style.transformOrigin = img.style.transformOrigin;
    }

    function zoomIn(ev) {
      if (zoomed) return;
      zoomed = true;
      small = img.getAttribute('src') || '';
      var big = bigSrc(small, needFor(SCALE));
      bigUrl = big;
      if (big && big !== small) {
        /* preload, so the swap never shows a half-decoded frame mid-zoom */
        var pre = new Image();
        pre.onload = function () { if (zoomed) img.src = big; };
        pre.src = big;
      }
      if (ev) originFrom(ev);
      box.style.setProperty('--gc-zoom-scale', String(SCALE));
      /* THE OVERLAY IS SCALED FROM HERE, NOT FROM THE STYLESHEET.
         The stylesheet rule names both the image and the initials overlay in
         one selector and the image obeys it, but the overlay was measured
         sitting at 266x266 against the image's 904x904 — its computed
         transform an identity matrix rather than the scale it was given. The
         letters were therefore left at 14.7% of the picture instead of 50%,
         which is the drift this whole mechanism exists to prevent.
         An inline transform is the one place nothing can outrank, and this
         file already owns the transform-origin for both elements, so the two
         values that have to agree now live side by side. */
      if (ov) ov.style.transform = 'scale(' + SCALE + ')';
      box.classList.add('is-zoomed');
      box.setAttribute('aria-label', 'Zoomed. Click to zoom out.');
    }

    function zoomOut() {
      if (!zoomed) return;
      zoomed = false;
      box.classList.remove('is-zoomed');
      img.style.transformOrigin = '';
      if (ov) { ov.style.transformOrigin = ''; ov.style.transform = ''; }
      if (small) img.src = small;
      box.setAttribute('aria-label', 'Click to zoom in');
    }

    box.addEventListener('click', function (ev) {
      /* the initials overlay and the corner notes are pointer-events:none, so
         anything that reaches here is the picture itself */
      zoomed ? zoomOut() : zoomIn(ev);
    });
    box.addEventListener('mousemove', function (ev) { if (zoomed) originFrom(ev); });
    box.addEventListener('touchmove', function (ev) {
      if (!zoomed) return;
      originFrom(ev);
      ev.preventDefault();      /* pan the picture rather than scroll the drawer */
    }, { passive: false });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && zoomed) zoomOut();
    });

    /* A variant change rewrites src underneath us; so does closing the drawer.
       Either way the zoom must not survive, or the restore would put back a
       stale image. */
    var drawer = document.querySelector('[data-personalize-drawer]');
    if (drawer && window.MutationObserver) {
      new MutationObserver(function () {
        if (!drawer.classList.contains('open')) zoomOut();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (zoomed && img.getAttribute('src') !== bigUrl) zoomOut();
      }).observe(img, { attributes: true, attributeFilter: ['src'] });
    }

    /* keyboard: the container is not focusable by default */
    box.setAttribute('tabindex', '0');
    box.setAttribute('role', 'button');
    box.setAttribute('aria-label', 'Click to zoom in');
    box.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); zoomed ? zoomOut() : zoomIn(null); }
    });
    return true;
  }

  /* The drawer is hydrated from a <template> at DOMContentLoaded, so the
     container may not exist yet on first try. */
  if (!boot()) {
    var tries = 0;
    var t = setInterval(function () {
      if (boot() || ++tries > 40) clearInterval(t);
    }, 150);
    document.addEventListener('DOMContentLoaded', boot);
  }
})();
